<template>
  <el-dialog title="Create a wave" :visible.sync="open" :before-close="close" width="390px">
    <div v-if="ready" class="flex flex-wrap mb-6">
      <div class="w-full mb-6">
        <label for="name" class="mb-2 font-bold block">Name</label>
        <input
          class="py-2 px-4 block w-full bg-gray-100 border border-gray-100 rounded focus:outline-none focus:bg-white focus:border-gray-500"
          :class="{'border-red-500': fieldsError.name ? true : false}" id="name" v-model="fields.name" type="text"
          placeholder="Welcome tour" required />
        <p v-if="fieldsError.name" class="text-red-500 font-bold">{{fieldsError.name}}</p>
      </div>

      <div class="w-full mb-6">
        <label for="url_start" class="mb-2 font-bold block">Start building this wave on</label>
        <input
          class="py-2 px-4 block w-full bg-gray-100 border border-gray-100 rounded focus:outline-none focus:bg-white focus:border-gray-500"
          :class="{'border-red-500': fieldsError.name ? true : false}" id="url_start" v-on:blur="checkGoodDomain()"
          v-model="fields.url" type="text" :placeholder="_extractHostname(site.url)" required />
        <p v-if="fieldsError.url" class="text-red-500 font-bold">{{fieldsError.url}}</p>
      </div>
      <div class="flex items-center w-full mb-4">
        <div>
          <span class="font-bold align-middle">Wave with CSV imported</span>
        </div>
        <div @click="handleWithImportCsvChanged" class="w-14 h-7 flex items-center rounded-full mx-2 px-1"
          :class="{ 'bg-gray-300': !fields.iscsv, 'bg-green-700': fields.iscsv }">
          <div class="bg-white w-5 h-5 rounded-full shadow-md"
            :class="{ 'transform-none': !fields.iscsv, 'transform translate-x-7 mr-5' : fields.iscsv }"></div>
        </div>
      </div>
      <div v-if="fields.iscsv" class="flex align-center gap-2">
        <common-input-file @change="handleCsvFileChanged" accept=".csv"><span class="break-normal whitespace-nowrap min-w-max px-4 py-2 rounded bg-green-500 text-white max-w-max shadow-sm hover:shadow-md">import a csv</span></common-input-file>
        <span class="break-normal" v-if="csvFile"><strong>{{csvFile.name}}</strong> selected</span>
      </div>
      <p v-if="fieldsError.csvFile && fields.iscsv" class="text-red-500 font-bold">{{fieldsError.csvFile}}</p>
    </div>
    <div class="flex flex-row-reverse">
      <button @click="save()" class="loader-button px-4 py-2 rounded bg-yellow-500 text-blue-50 max-w-max shadow-sm hover:shadow-md">
        <img v-if="loading" src="../../../../assets/img/loading.gif" alt="Loading" />
        Let's go !
      </button>
      <button @click="close"
        class="px-4 py-2 rounded border border-gray-200 text-gray-500 max-w-max shadow-sm hover:shadow-md mr-3">
        Cancel
      </button>
    </div>
  </el-dialog>
</template>

<script>

import { mapActions, mapGetters } from 'vuex'

export default {
  name: "FormWave",
  props: {
    open: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      ready: false,
      fields: {
        name: null,
        url: null,
        iscsv: false,
      },
      fieldsError: {
        name: null,
        url: null,
        csvFile: null
      },
      saving: false,
      csvFile: null,
      loading: false,
    };
  },
  computed: {
    ...mapGetters("account", {
      site: "currentSite"
    })
  },
  methods: {
    ...mapActions("waves", ["addWave", "addCsvFile", "removeWave"]),
    handleWithImportCsvChanged() {
      this.fields.iscsv = !this.fields.iscsv;
    },
    handleCsvFileChanged(event) {
      this.csvFile = event.target.files[0];
    },
    save() {
      this.fieldsError.name = null;
      this.fieldsError.url = null;
      this.fieldsError.csvFile = null;
      if (!this.checkInputs()) {
        return false;
      }
      this.saving = true;
      this.loading = true;
      this.addWave({
        wave: {
          ...this.fields,
          frequency: "every_time",
          url_target: ".*",
          target_audience: "all",
          status: "disable",
        },
        sitekey: this.$route.params.site
      }).then(async (wave) => {
        if(this.fields.iscsv) {
          await this.addCsvFile({
            wave: wave,
            csvFile: this.csvFile,
            sitekey: this.$route.params.site
          }).catch((response) => {
            let message = response.response && response.response.data && response.response.data.error ? response.response.data.error : "An error occurred while importing the csv file"
            this.$notify({
              title: "Error",
              message: message,
              type: "error"
            });
            this.removeWave({wave: wave});
            throw {data: {code: 400}};
          })
        }
        this.$notify({
          title: "Success",
          message: this.$root.$t("notifications.wave.added"),
          type: "success"
        });
        this.close();
        this.$router.push({
          name: "Wave",
          params: { site: this.$route.params.site, id: wave.id },
          query: { add_step: 1 }
        });
        this.loading = false;
      })
        .catch((response) => {
          console.log("error", response);
          this.$notify.error({
            title: this.$root.$t("api." + response.data.code + ".title"),
            message: this.$root.$t("api." + response.data.code + ".message")
          });
          this.saving = false;
          this.loading = false;
        });
    },
    checkGoodDomain() {
      if (this.fields.url && this.fields.url.startsWith("https://")) {
        this.fields.url = this.fields.url.slice(8);
      }
      if (this.fields.url && this.fields.url.startsWith("http://")) {
        this.fields.url = this.fields.url.slice(7);
      }
      if (this.fields.url && this.fields.url.endsWith("/")) {
        this.fields.url = this.fields.url.slice(0, -1);
      }
      let i = 0;
      while (this.fields.url && this.fields.url[i] !== "/" && i <= this.fields.url.length) {
        ++i;
      }
      if (this.fields.url) {
        this.fields.url = this.fields.url.slice(0, i);
      }
    },
    // eslint-disable-next-line
    checkInputs() {
      this.checkGoodDomain();
      const ValidHostnameRegex = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/;
      let error = false;
      if (!this.fields.name) {
        this.fieldsError.name = "Name must be a valid string, like: Welcome dear !";
        error = true;
      }
      if (!this.fields.url || !Array.isArray(this.fields.url.match(ValidHostnameRegex)) || (Array.isArray(this.fields.url.match(ValidHostnameRegex)) && !this.fields.url.match(ValidHostnameRegex).length)) {
        this.fieldsError.url = "Hostname must be a valid string, like: rocketfid.com";
        error = true;
      }
      if (this.fields.iscsv && this.csvFile === null) {
        this.fieldsError.csvFile = "You must provide a CSV file";
        error = true;
      }
      return !error;
    },
    close() {
      this.$emit("close", true);
    }
  },
  mounted() {
    this.ready = true;
  },
}
</script>

<style>
.loader-button {
  display: flex;
  align-items: center;
}

.loader-button img {
  margin-right: 8px;
}
</style>


// WEBPACK FOOTER //
// src/components/waves/ui/modal/create.vue